import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Sparkles, ArrowRight, MessageCircle, ShieldCheck, Lock, Server, Cloud, CheckCircle2, ChevronDown, Play, Zap, QrCode, KeyRound, Check, X, Calendar } from 'lucide-react';
import { useState } from "react";
import { NamedTestimonials } from "@/components/marketing/NamedTestimonials";
import { AppStoreBadges } from "@/components/marketing/AppStoreBadges";
import { DemoBookingSheet } from "@/components/marketing/DemoBookingSheet";
import { DemoVideoUnavailableDialog } from "@/components/marketing/DemoVideoUnavailableDialog";
import { PublicSignupCta } from "@/components/auth/PublicSignupCta";
import { Logo } from "@/components/common/Logo";

// Phase 10 — Universal landing rendered at `/` for unauthenticated visitors.
// 13 sections, modeled after Linear/Stripe-style editorial pages.

const TRUSTED = ["Linear", "Stripe", "Notion", "Vercel", "Ramp", "Mercury"];

function TopNav() {
  return (
    <nav className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200/70">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2 sm:gap-4 min-w-0">
        <Link to="/" className="shrink-0" data-testid="nav-logo">
          <Logo size={22} />
        </Link>
        <div className="ml-auto flex items-center gap-0.5 sm:gap-1 min-w-0">
          <a href="#pricing" className="hidden sm:inline text-[12.5px] font-medium text-slate-600 hover:text-slate-900 px-2 sm:px-2.5 py-1.5" data-testid="nav-pricing">Pricing</a>
          <Link to="/login" className="text-[12.5px] font-medium text-slate-600 hover:text-slate-900 px-2 sm:px-2.5 py-1.5" data-testid="nav-signin">Sign in</Link>
          <PublicSignupCta
            to="/signup"
            buttonProps={{ size: "sm", className: "h-9 sm:h-8 text-[12px] ml-1 sm:ml-2", "data-testid": "nav-cta" }}
          >
            <span className="sm:hidden">Trial</span>
            <span className="hidden sm:inline">Start your trial</span> <ArrowRight size={12} />
          </PublicSignupCta>
        </div>
      </div>
    </nav>
  );
}

function BriefingHeroCard() {
  return (
    <div className="rounded-3xl bg-gradient-to-br from-indigo-100/70 via-violet-100/40 to-emerald-50/40 ring-1 ring-slate-200/60 shadow-sm p-4 sm:p-8 min-w-0">
      <div className="rounded-2xl bg-white shadow-lg ring-1 ring-slate-200 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold inline-flex items-center gap-1">
            <Sparkles size={10} /> Maya · Overnight
          </div>
          <span className="text-[9.5px] text-slate-400 font-mono shrink-0">07:31 AM</span>
        </div>
        <div className="mt-2 text-[14px] sm:text-[15px] font-semibold text-slate-900 leading-snug">Good morning, Aman. While you slept, I replied to <span className="text-indigo-700">7</span> WhatsApp messages and booked <span className="text-indigo-700">2</span> demos.</div>
        <div className="mt-3 space-y-1.5">
          {[
            "Replied to Sarah (Linear) — pricing question",
            "Booked Helen (Stripe) for Thursday 2pm",
            "Drafted 3 LinkedIn follow-ups (awaiting approval)",
            "Flagged 1 hot signal from r/SaaS",
          ].map((l) => (
            <div key={l} className="flex items-center gap-2 text-[12px] text-slate-700"><CheckCircle2 size={12} className="text-emerald-500 shrink-0" /><span className="min-w-0 break-words">{l}</span></div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          {[
            { k: "Saved", v: "2h 40m" },
            { k: "Replies", v: "7" },
            { k: "Demos", v: "2" },
          ].map((s) => (
            <div key={s.k} className="rounded-md bg-slate-50/80 ring-1 ring-slate-200 py-1.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{s.k}</div>
              <div className="text-[14px] font-semibold tabular-nums text-slate-900">{s.v}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">Powered by OpenClaw · v2.4.1</div>
      </div>
    </div>
  );
}

function FAQItem({ q, a, idx, open, setOpen }) {
  return (
    <button onClick={() => setOpen(open === idx ? -1 : idx)} className="w-full text-left px-5 py-4 hover:bg-slate-50/50 transition-colors" data-testid={`landing-faq-${idx}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13.5px] font-semibold text-slate-900">{q}</span>
        <ChevronDown size={14} className={cn("text-slate-400 transition-transform shrink-0", open === idx && "rotate-180")} />
      </div>
      {open === idx && <div className="mt-2 text-[12.5px] text-slate-600 leading-relaxed">{a}</div>}
    </button>
  );
}
FAQItem.propTypes = {
  q: PropTypes.string.isRequired,
  a: PropTypes.string.isRequired,
  idx: PropTypes.number.isRequired,
  open: PropTypes.number.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default function UniversalLanding() {
  const [openFaq, setOpenFaq] = useState(0);
  const [demoOpen, setDemoOpen] = useState(false);
  const [videoUnavailableOpen, setVideoUnavailableOpen] = useState(false);
  const faqs = [
    { q: "Is this just a chatbot?",                a: "No. Maya is a fully autonomous AI SDR that runs on your real personal accounts and operates with your voice. Customers can&rsquo;t tell they&rsquo;re talking to AI." },
    { q: "Do I need to know APIs or programming?", a: "No. Pair WhatsApp with a QR scan, connect LinkedIn with one click. Setup takes 5 minutes." },
    { q: "Will my WhatsApp get banned?",            a: "Maya operates within humanlike pacing limits, varies send times, and respects platform rate ceilings. Your safety dashboard shows live ban-risk per account. That said, automation on personal accounts may violate platform TOS — you&rsquo;ll acknowledge this risk during setup." },
    { q: "Who can see my data?",                    a: "Only you. Sellyx runs in a dedicated instance just for you. We have zero remote access without your invitation." },
    { q: "Can my team use the same Maya?",          a: "Yes. Add up to 5 collaborators. Each gets their own login but shares one Maya." },
    { q: "What if I want to host it myself?",       a: "Pick the Self-hosted Cloud tier. Maya runs on your AWS or GCP." },
    { q: "How does pricing work?",                  a: "Flat monthly fee per instance. No per-message or per-user gotchas." },
  ];

  return (
    <div className="bg-white" data-testid="universal-landing">
      <TopNav />

      {/* 1. Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-10 sm:pb-12 grid md:grid-cols-2 gap-8 sm:gap-10 items-center min-w-0">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 ring-1 ring-indigo-200 px-2.5 py-0.5 text-[10.5px] font-semibold text-indigo-700">
            <Sparkles size={10} /> Powered by OpenClaw
          </div>
          <h1 className="mt-4 text-[32px] sm:text-[44px] md:text-[64px] font-semibold tracking-tight text-slate-900 leading-[1.05] sm:leading-[1.02]" data-testid="landing-headline">
            Hire your AI SDR. In 5 minutes. Not 5 months.
          </h1>
          <p className="mt-4 sm:mt-5 text-[15px] sm:text-[16px] md:text-[18px] text-slate-600 leading-relaxed max-w-xl">
            Sellyx is a personal AI sales agent for founders. She works on your WhatsApp, LinkedIn, and Gmail — <span className="font-semibold text-slate-800">as you</span>.
            No APIs, no learning curve. Just connect your channels and she gets to work.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-wrap">
            <PublicSignupCta
              to="/signup"
              buttonProps={{ size: "lg", className: "h-11 text-[13px] px-5 w-full sm:w-auto", "data-testid": "landing-cta-trial" }}
            >
              Start free trial <ArrowRight size={13} />
            </PublicSignupCta>
            <Button variant="outline" size="lg" className="h-11 text-[13px] px-4 w-full sm:w-auto" data-testid="landing-cta-demo" onClick={() => setVideoUnavailableOpen(true)}><Play size={12} /> Watch a 2-min demo</Button>
          </div>
          <div className="mt-5 inline-flex items-center gap-2 text-[11.5px] text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> No credit card · cancel anytime
          </div>
        </div>
        <BriefingHeroCard />
      </section>

      {/* 2. Trust strip */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
        <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
          <span className="uppercase tracking-wider text-[10px] font-semibold text-slate-400">Trusted by founders building</span>
          <div className="flex items-center gap-4 flex-wrap">
            {TRUSTED.map((t) => <span key={t} className="font-semibold tracking-tight text-slate-700">{t}</span>)}
          </div>
        </div>
      </section>

      {/* 3. What is Sellyx — 3 pillars */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold text-center">What is Sellyx?</div>
        <h2 className="mt-1 text-[24px] sm:text-[28px] md:text-[34px] font-semibold tracking-tight text-slate-900 text-center max-w-3xl mx-auto">Your personal AI SDR — on your channels, in your voice.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { Icon: MessageCircle, title: "She works as you on your channels", body: "WhatsApp, LinkedIn, Gmail — Maya logs in as you. Your customers think they&rsquo;re talking to you, because they are." },
            { Icon: Zap,           title: "She replies in seconds, 24/7",       body: "While you sleep, demo, or breathe. Maya never misses an inbound, never forgets a follow-up." },
            { Icon: ShieldCheck,   title: "Your data, your instance, your control", body: "Sellyx runs in a dedicated instance just for you. SOC 2, GDPR, AES-256. Your conversations never leave." },
          ].map((p) => (
            <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center"><p.Icon size={16} /></div>
              <div className="mt-3 text-[14px] font-semibold text-slate-900">{p.title}</div>
              <div className="text-[12px] text-slate-600 mt-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: p.body }} />
            </div>
          ))}
        </div>
      </section>

      {/* 4. How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12 rounded-3xl bg-slate-50/40">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold text-center">How it works</div>
        <h2 className="mt-1 text-[22px] sm:text-[26px] md:text-[30px] font-semibold tracking-tight text-slate-900 text-center">Three steps. One afternoon.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { Icon: KeyRound,     n: 1, title: "Sign up",            body: "5 minutes. Email + password." },
            { Icon: QrCode,       n: 2, title: "Pair your channels", body: "WhatsApp QR scan. Gmail OAuth. LinkedIn one-click. Done." },
            { Icon: Sparkles,     n: 3, title: "Maya starts working", body: "Inbound replies, outbound sequences, signal-driven outreach. All in your voice." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 rounded-full bg-indigo-600 text-white text-[11px] font-semibold items-center justify-center">{s.n}</span>
                <s.Icon size={14} className="text-indigo-600" />
              </div>
              <div className="mt-3 text-[14px] font-semibold text-slate-900">{s.title}</div>
              <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">{s.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 4b. Sellyx vs the alternatives */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="competitor-table">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold text-center">Comparison</div>
        <h2 className="mt-1 text-[22px] sm:text-[26px] md:text-[30px] font-semibold tracking-tight text-slate-900 text-center">Sellyx vs the alternatives</h2>
        <p className="mt-2 text-[13px] text-slate-500 text-center max-w-xl mx-auto">Founders compare us most often against hiring SDRs and traditional sequencing tools. Here&rsquo;s how we stack up.</p>
        <div className="mt-8 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <table className="w-full min-w-[640px] text-[12.5px] border-collapse">
            <thead>
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-500 uppercase tracking-wider text-[10.5px] w-[24%]">Attribute</th>
                <th className="text-left px-4 py-3 w-[25.3%]">
                  <div className="rounded-t-xl bg-indigo-50 ring-1 ring-indigo-300 px-3 py-2 border-b-0">
                    <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold">The smart choice</div>
                    <div className="text-[14px] font-semibold text-slate-900">Sellyx</div>
                  </div>
                </th>
                <th className="text-left px-4 py-3 w-[25.3%]">
                  <div className="rounded-t-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Traditional</div>
                    <div className="text-[14px] font-semibold text-slate-700">Hiring SDRs</div>
                  </div>
                </th>
                <th className="text-left px-4 py-3 w-[25.3%]">
                  <div className="rounded-t-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Sequencing tool</div>
                    <div className="text-[14px] font-semibold text-slate-700">Outreach.io</div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: "Cost",                       s: { ok: true,  text: "$999/mo" },     sdr: { ok: false, text: "$8-12k/mo per SDR" },  out: { ok: false, text: "$130/seat/mo" } },
                { label: "Setup time",                 s: { ok: true,  text: "5 minutes" },   sdr: { ok: false, text: "5-7 months to ramp" }, out: { ok: false, text: "2-4 weeks integration" } },
                { label: "Personal accounts (channels)", s: { ok: true,  text: "WhatsApp · LinkedIn · Gmail · SMS" }, sdr: { ok: true, text: "Yes, but expensive" }, out: { ok: false, text: "Business APIs only" } },
                { label: "24/7 coverage",              s: { ok: true,  text: "Always-on" },   sdr: { ok: false, text: "9-6 weekdays" },       out: { ok: false, text: "No — scheduled only" } },
                { label: "Team integration",           s: { ok: true,  text: "5 collaborators, shared Maya" }, sdr: { ok: true,  text: "Manual handoffs" },     out: { ok: true, text: "Yes, with seat licensing" } },
                { label: "Data residency",             s: { ok: true,  text: "Your instance · SOC 2" }, sdr: { ok: true, text: "Office" }, out: { ok: false, text: "Shared multi-tenant SaaS" } },
              ].map((row, i) => (
                <tr key={row.label} className={cn("border-t border-slate-200", i % 2 === 1 ? "bg-slate-50/30" : "bg-white")} data-testid={`compare-row-${i}`}>
                  <td className="px-4 py-3 font-semibold text-slate-700">{row.label}</td>
                  <td className="px-4 py-3 ring-1 ring-indigo-100 bg-indigo-50/40">
                    <div className="flex items-start gap-2"><Check size={12} className="text-emerald-600 mt-0.5 shrink-0" /><span className="text-slate-900 font-medium">{row.s.text}</span></div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">{row.sdr.ok ? <Check size={12} className="text-emerald-600 mt-0.5 shrink-0" /> : <X size={12} className="text-slate-400 mt-0.5 shrink-0" />}<span className="text-slate-600">{row.sdr.text}</span></div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">{row.out.ok ? <Check size={12} className="text-emerald-600 mt-0.5 shrink-0" /> : <X size={12} className="text-slate-400 mt-0.5 shrink-0" />}<span className="text-slate-600">{row.out.text}</span></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 5. A day with Maya */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold text-center">A day with Maya</div>
        <h2 className="mt-1 text-[22px] sm:text-[26px] md:text-[30px] font-semibold tracking-tight text-slate-900 text-center max-w-2xl mx-auto px-1">You walk in to fewer decisions and clearer outcomes.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 items-center min-w-0">
          <BriefingHeroCard />
          <blockquote className="text-[15px] md:text-[17px] text-slate-700 leading-relaxed italic">
            &ldquo;By 7am, I&rsquo;d handled 7 inbound WhatsApp messages, booked 2 demos for tomorrow, drafted 3 LinkedIn follow-ups, and flagged a hot signal from r/SaaS.
            You walk into the office to <span className="not-italic font-semibold text-slate-900">11 fresh decisions, not 60 chaotic ones</span>.&rdquo;
            <div className="mt-3 text-[12px] not-italic text-slate-500">— Maya, 7:31am briefing</div>
          </blockquote>
        </div>
      </section>

      {/* 6. The numbers */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 border-y border-slate-200/60">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { value: "4,842",  label: "Conversations Maya handled this month" },
            { value: "1,184h", label: "Of SDR time saved" },
            { value: "$842k",  label: "Pipeline attributed to Maya" },
            { value: "5.2",    label: "FTE SDR equivalent replaced per founder" },
          ].map((s) => (
            <div key={s.label}>
              <div className="text-[24px] sm:text-[32px] md:text-[40px] font-semibold tracking-tight text-indigo-700 tabular-nums leading-none">{s.value}</div>
              <div className="text-[11px] text-slate-500 mt-1.5 max-w-[180px] mx-auto leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 7. Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold text-center">Pricing</div>
        <h2 className="mt-1 text-[22px] sm:text-[26px] md:text-[32px] font-semibold tracking-tight text-slate-900 text-center max-w-2xl mx-auto px-1">Two ways to deploy. Same Maya. Pick what fits.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { id: "saas",     Icon: Cloud, accent: "from-indigo-500 to-violet-600",   title: "Private SaaS",       price: "$999/mo",            tagline: "Best for most founders", badge: true,  cta: "Start trial", to: "/onboarding/welcome", features: ["Single-tenant container", "All channels", "5 collaborators", "99.97% uptime SLA"] },
            { id: "cloud",    Icon: Server, accent: "from-emerald-500 to-teal-600",   title: "Self-hosted Cloud",  price: "$1,499/mo",          tagline: "Your AWS/GCP · BYO KMS",              cta: "Self-host", to: "/marketing/cloud", features: ["Deploy into your VPC", "Bring your own KMS", "Custom audit logs", "10 collaborators"] },
          ].map((c) => (
            <div key={c.id} className={cn("rounded-2xl border bg-white p-5 relative", c.badge ? "border-indigo-300 ring-2 ring-indigo-200/50 shadow-sm" : "border-slate-200")} data-testid={`landing-pricing-${c.id}`}>
              {c.badge && <div className="absolute -top-2.5 left-5 inline-flex items-center gap-1 rounded-full bg-indigo-600 text-white px-2 py-0.5 text-[9.5px] font-semibold tracking-wide uppercase">{c.tagline}</div>}
              <div className={cn("h-9 w-9 rounded-lg text-white flex items-center justify-center bg-gradient-to-br", c.accent)}><c.Icon size={16} /></div>
              <div className="mt-3 text-[14.5px] font-semibold text-slate-900">{c.title}</div>
              {!c.badge && <div className="text-[11.5px] text-slate-500">{c.tagline}</div>}
              <div className="mt-1.5 text-[22px] font-semibold tabular-nums text-indigo-700">{c.price}</div>
              <ul className="mt-3 space-y-1 text-[11.5px] text-slate-700">
                {c.features.map((f) => <li key={f} className="inline-flex items-center gap-1.5"><CheckCircle2 size={10} className="text-emerald-600" /> {f}</li>)}
              </ul>
              <Link to={c.to}><Button size="sm" className="w-full mt-4 h-10 sm:h-8 text-[11.5px]" variant={c.badge ? "default" : "outline"} data-testid={`landing-pricing-cta-${c.id}`}>{c.cta} <ArrowRight size={11} /></Button></Link>
            </div>
          ))}
        </div>
        <div className="mt-4 text-center text-[11.5px] text-slate-500">All tiers include unlimited messages, all channels, the full team (5 seats), and 99.97% uptime.</div>
      </section>

      {/* 8. Named testimonials */}
      <NamedTestimonials eyebrow="Loved by founders" />

      {/* 9. Security & trust */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 border-y border-slate-200/60">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { Icon: ShieldCheck, label: "SOC 2 Type II" },
              { Icon: Lock,        label: "GDPR" },
              { Icon: Server,      label: "AES-256 at rest" },
              { Icon: Lock,        label: "TLS 1.3 in transit" },
            ].map((p) => (
              <span key={p.label} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700"><p.Icon size={10} /> {p.label}</span>
            ))}
          </div>
          <div className="text-[10.5px] text-slate-400">Powered by <a href="https://openclaw.ai" target="_blank" rel="noreferrer noopener" className="font-mono text-indigo-700 hover:text-indigo-800">OpenClaw</a></div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <h2 className="text-[22px] sm:text-[24px] md:text-[28px] font-semibold tracking-tight text-slate-900 text-center mb-6">Common questions</h2>
        <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-200">
          {faqs.map((it, i) => <FAQItem key={it.q} q={it.q} a={it.a} idx={i} open={openFaq} setOpen={setOpenFaq} />)}
        </div>
      </section>

      {/* 11. Final CTA gradient strip */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
        <div className="rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-indigo-700 text-white p-6 sm:p-10 md:p-12 text-center shadow-lg">
          <h2 className="text-[24px] sm:text-[28px] md:text-[36px] font-semibold tracking-tight leading-tight">Stop hiring SDRs. Hire Maya.</h2>
          <p className="mt-3 text-[14px] md:text-[15px] text-indigo-100 max-w-lg mx-auto leading-relaxed">5-minute setup. Full free trial. Cancel anytime.</p>
          <PublicSignupCta
            to="/signup"
            buttonProps={{
              size: "lg",
              className: "mt-6 h-11 text-[13px] px-5 w-full sm:w-auto bg-white text-indigo-700 hover:bg-indigo-50",
              "data-testid": "landing-final-cta",
            }}
          >
            Start your free trial <ArrowRight size={13} />
          </PublicSignupCta>
          <div className="mt-4">
            <button type="button" onClick={() => setDemoOpen(true)} className="inline-flex items-center gap-1.5 text-[12.5px] text-indigo-100 underline-offset-2 hover:underline" data-testid="landing-book-demo">
              <Calendar size={12} /> Or book a 30-min call with our team
            </button>
          </div>
        </div>
      </section>

      {/* 12. Footer */}
      <footer className="border-t border-slate-200 bg-slate-50/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-[12px] text-slate-600">
          <div>
            <Logo size={18} />
            <div className="mt-2 text-[11px] text-slate-500">Your personal AI SDR.</div>
            <div className="mt-3">
              <AppStoreBadges stacked />
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Product</div>
            <Link to="/marketing/saas"     className="block py-0.5 hover:text-slate-900">Private SaaS</Link>
            <Link to="/marketing/cloud"    className="block py-0.5 hover:text-slate-900">Self-hosted Cloud</Link>
            <Link to="/login"               className="block py-0.5 hover:text-slate-900">Sign in</Link>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Trust</div>
            <Link to="/privacy" className="block py-0.5 hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="block py-0.5 hover:text-slate-900">Terms</Link>
            <Link to="/security" className="block py-0.5 hover:text-slate-900">Security</Link>
            <Link to="/status" className="block py-0.5 hover:text-slate-900">Status</Link>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Contact</div>
            <Link to="/contact" className="block py-0.5 hover:text-slate-900">hello@sellix.ai</Link>
            <div>San Francisco · London · Bengaluru</div>
            <div className="mt-2 text-[10px] text-slate-400">© 2026 Sellyx. <a href="https://openclaw.ai" target="_blank" rel="noreferrer noopener" className="hover:text-slate-700">Powered by OpenClaw</a>.</div>
          </div>
        </div>
      </footer>

      <DemoBookingSheet open={demoOpen} onOpenChange={setDemoOpen} />
      <DemoVideoUnavailableDialog open={videoUnavailableOpen} onOpenChange={setVideoUnavailableOpen} onBookDemo={() => setDemoOpen(true)} />
    </div>
  );
}
